import * as THREE from 'three';
import { BaseCardScene, BaseCardSceneConfig } from './base-card-scene';
import { CardOrientation, CardHelper } from '../card-orientation';

/**
 * Configuration for card outline scene
 */
export interface CardOutlineSceneConfig extends BaseCardSceneConfig {
  sceneId?: string;
  name?: string;
  description?: string;

  // Visual options
  borderColor?: number;
  borderWidth?: number;
  cornerSize?: number;
  showCornerMarkers?: boolean;
  showAxes?: boolean;
  showDimensions?: boolean;
  showOrientationLabel?: boolean;
  labelColor?: string;
  labelBackgroundColor?: string;

  // Animation
  animate?: boolean;
  pulseSpeed?: number;
}

/**
 * Card Outline Test Scene
 *
 * Displays a visual outline of the tracked business card to verify
 * that portrait and landscape anchors are recognized and delimited correctly.
 *
 * Features:
 * - Border outline showing exact card boundaries
 * - Corner markers for precise positioning
 * - Orientation label (LANDSCAPE or PORTRAIT)
 * - Dimension display
 * - Optional coordinate axes
 * - Optional pulsing animation for visibility
 */
export class CardOutlineScene extends BaseCardScene {
  private borderLines: THREE.LineSegments[] = [];
  private cornerMarkers: THREE.Mesh[] = [];
  private orientationLabel: THREE.Mesh | null = null;
  private dimensionLabel: THREE.Mesh | null = null;
  private axes: THREE.Group | null = null;

  // Configuration
  private borderColor: number;
  private borderWidth: number;
  private cornerSize: number;
  private showCornerMarkers: boolean;
  private showAxes: boolean;
  private showDimensions: boolean;
  private showOrientationLabel: boolean;
  private labelColor: string;
  private labelBackgroundColor: string;
  private animate: boolean;
  private pulseSpeed: number;

  // Detected orientation
  private orientation: CardOrientation;

  constructor(config: CardOutlineSceneConfig = {}) {
    const orientation = config.cardWidth && config.cardHeight
      ? (config.cardWidth >= config.cardHeight ? CardOrientation.LANDSCAPE : CardOrientation.PORTRAIT)
      : CardOrientation.LANDSCAPE;

    super(
      {
        id: config.sceneId || `card-outline-${orientation}`,
        name: config.name || `Card Outline Test (${CardHelper.getOrientationLabel(orientation)})`,
        description: config.description || `Visual outline for ${orientation} card boundary verification`
      },
      config
    );

    this.orientation = orientation;

    // Visual configuration
    this.borderColor = config.borderColor ?? 0x00ff00;
    this.borderWidth = config.borderWidth ?? 3;
    this.cornerSize = config.cornerSize ?? 0.08;
    this.showCornerMarkers = config.showCornerMarkers ?? true;
    this.showAxes = config.showAxes ?? true;
    this.showDimensions = config.showDimensions ?? true;
    this.showOrientationLabel = config.showOrientationLabel ?? true;
    this.labelColor = config.labelColor ?? '#00ff00';
    this.labelBackgroundColor = config.labelBackgroundColor ?? 'rgba(0, 0, 0, 0.7)';
    this.animate = config.animate ?? true;
    this.pulseSpeed = config.pulseSpeed ?? 2.0;
  }

  init(parent: THREE.Object3D, renderer: THREE.WebGLRenderer): void {
    if (this.initialized) return;

    this.renderer = renderer;
    parent.add(this.root);

    // Add ambient light
    this.addAmbientLight(0xffffff, 1.0);

    // Create border outline
    this.createBorder();

    // Create corner markers
    if (this.showCornerMarkers) {
      this.createCornerMarkers();
    }

    // Create coordinate axes
    if (this.showAxes) {
      this.createAxes();
    }

    // Create orientation label
    if (this.showOrientationLabel) {
      this.createOrientationLabel();
    }

    // Create dimension label
    if (this.showDimensions) {
      this.createDimensionLabel();
    }

    this.initialized = true;
  }

  /**
   * Create border outline around card edges
   */
  private createBorder(): void {
    const bounds = this.getCardBounds();
    const thickness = 0.005; // Slight elevation above card

    // Create four border lines (top, right, bottom, left)
    const borders = [
      // Top
      { start: [bounds.left, thickness, bounds.top], end: [bounds.right, thickness, bounds.top] },
      // Right
      { start: [bounds.right, thickness, bounds.top], end: [bounds.right, thickness, bounds.bottom] },
      // Bottom
      { start: [bounds.right, thickness, bounds.bottom], end: [bounds.left, thickness, bounds.bottom] },
      // Left
      { start: [bounds.left, thickness, bounds.bottom], end: [bounds.left, thickness, bounds.top] }
    ];

    borders.forEach(border => {
      const points = [
        new THREE.Vector3(...border.start),
        new THREE.Vector3(...border.end)
      ];

      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const material = new THREE.LineBasicMaterial({
        color: this.borderColor,
        linewidth: this.borderWidth,
        transparent: true,
        opacity: 1.0
      });

      const line = new THREE.LineSegments(geometry, material);
      this.root.add(line);
      this.borderLines.push(line);

      this.trackGeometry(geometry);
      this.trackMaterial(material);
    });
  }

  /**
   * Create corner markers at each corner of the card
   */
  private createCornerMarkers(): void {
    const bounds = this.getCardBounds();
    const size = this.cornerSize;
    const thickness = 0.01;

    const corners = [
      { x: bounds.left, z: bounds.top, name: 'TL' },     // Top-Left
      { x: bounds.right, z: bounds.top, name: 'TR' },    // Top-Right
      { x: bounds.right, z: bounds.bottom, name: 'BR' }, // Bottom-Right
      { x: bounds.left, z: bounds.bottom, name: 'BL' }   // Bottom-Left
    ];

    corners.forEach(corner => {
      // Create L-shaped corner marker using two boxes
      const markerGroup = new THREE.Group();

      // Horizontal part
      const hGeo = new THREE.BoxGeometry(size, 0.002, size / 10);
      const hMat = new THREE.MeshBasicMaterial({ color: this.borderColor });
      const hMesh = new THREE.Mesh(hGeo, hMat);
      markerGroup.add(hMesh);

      // Vertical part
      const vGeo = new THREE.BoxGeometry(size / 10, 0.002, size);
      const vMat = new THREE.MeshBasicMaterial({ color: this.borderColor });
      const vMesh = new THREE.Mesh(vGeo, vMat);
      markerGroup.add(vMesh);

      // Position the marker group
      markerGroup.position.set(corner.x, thickness, corner.z);

      this.root.add(markerGroup);
      this.cornerMarkers.push(hMesh, vMesh);

      this.trackGeometry(hGeo);
      this.trackGeometry(vGeo);
      this.trackMaterial(hMat);
      this.trackMaterial(vMat);
    });
  }

  /**
   * Create coordinate axes (X=red, Y=green, Z=blue)
   */
  private createAxes(): void {
    this.axes = new THREE.Group();

    const axisLength = Math.min(this.cardWidth, this.cardHeight) * 0.3;
    const axisThickness = 0.01;

    // X axis (red) - points right
    const xGeo = new THREE.CylinderGeometry(axisThickness / 2, axisThickness / 2, axisLength);
    const xMat = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    const xAxis = new THREE.Mesh(xGeo, xMat);
    xAxis.rotation.z = -Math.PI / 2;
    xAxis.position.set(axisLength / 2, 0.015, 0);
    this.axes.add(xAxis);

    // Y axis (green) - points up
    const yGeo = new THREE.CylinderGeometry(axisThickness / 2, axisThickness / 2, axisLength);
    const yMat = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
    const yAxis = new THREE.Mesh(yGeo, yMat);
    yAxis.position.set(0, axisLength / 2 + 0.015, 0);
    this.axes.add(yAxis);

    // Z axis (blue) - points toward camera
    const zGeo = new THREE.CylinderGeometry(axisThickness / 2, axisThickness / 2, axisLength);
    const zMat = new THREE.MeshBasicMaterial({ color: 0x0000ff });
    const zAxis = new THREE.Mesh(zGeo, zMat);
    zAxis.rotation.x = Math.PI / 2;
    zAxis.position.set(0, 0.015, axisLength / 2);
    this.axes.add(zAxis);

    this.root.add(this.axes);

    this.trackGeometry(xGeo);
    this.trackGeometry(yGeo);
    this.trackGeometry(zGeo);
    this.trackMaterial(xMat);
    this.trackMaterial(yMat);
    this.trackMaterial(zMat);
  }

  /**
   * Create orientation label
   */
  private createOrientationLabel(): void {
    const orientationText = CardHelper.getOrientationLabel(this.orientation).toUpperCase();

    const { mesh, canvas, ctx, texture } = this.createTextMesh({
      text: orientationText,
      font: 'Arial',
      fontSize: 120,
      color: this.labelColor,
      width: 512,
      height: 256,
      backgroundColor: this.labelBackgroundColor,
      textAlign: 'center',
      textBaseline: 'middle'
    });

    // Scale the mesh to fit on card
    const labelWidth = this.cardWidth * 0.5;
    const labelHeight = labelWidth * 0.5;
    mesh.scale.set(labelWidth, labelHeight, 1);

    // Position at center, slightly above card
    mesh.position.set(0, 0.02, 0);
    mesh.rotation.x = -Math.PI / 2; // Face up

    this.root.add(mesh);
    this.orientationLabel = mesh;
  }

  /**
   * Create dimension label showing width x height
   */
  private createDimensionLabel(): void {
    const dimensionText = `${this.cardWidth.toFixed(2)} × ${this.cardHeight.toFixed(2)}`;

    const { mesh, canvas, ctx, texture } = this.createTextMesh({
      text: dimensionText,
      font: 'monospace',
      fontSize: 80,
      color: this.labelColor,
      width: 512,
      height: 128,
      backgroundColor: this.labelBackgroundColor,
      textAlign: 'center',
      textBaseline: 'middle'
    });

    // Scale the mesh
    const labelWidth = this.cardWidth * 0.4;
    const labelHeight = labelWidth * 0.25;
    mesh.scale.set(labelWidth, labelHeight, 1);

    // Position below orientation label
    mesh.position.set(0, 0.02, this.cardHeight * 0.25);
    mesh.rotation.x = -Math.PI / 2; // Face up

    this.root.add(mesh);
    this.dimensionLabel = mesh;
  }

  update(delta: number): void {
    this.elapsedTime += delta;

    // Optional pulsing animation
    if (this.animate) {
      const pulse = Math.sin(this.elapsedTime * this.pulseSpeed) * 0.2 + 0.8;

      // Pulse border lines
      this.borderLines.forEach(line => {
        if (line.material instanceof THREE.LineBasicMaterial) {
          line.material.opacity = pulse;
        }
      });

      // Pulse corner markers
      this.cornerMarkers.forEach(marker => {
        if (marker.material instanceof THREE.MeshBasicMaterial) {
          marker.material.opacity = pulse;
        }
      });

      // Pulse labels
      if (this.orientationLabel) {
        this.orientationLabel.material.opacity = pulse;
      }
      if (this.dimensionLabel) {
        this.dimensionLabel.material.opacity = pulse;
      }
    }
  }

  dispose(): void {
    this.borderLines = [];
    this.cornerMarkers = [];
    this.orientationLabel = null;
    this.dimensionLabel = null;
    this.axes = null;

    super.dispose();
  }

  /**
   * Override setVisible to reset animation timing
   */
  setVisible(visible: boolean): void {
    super.setVisible(visible);
    if (visible) {
      this.resetElapsedTime();
    }
  }
}
